/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "../../providers";
import { parseTableCreditError } from "./tableCredit";

export type TableCreditCloseDialogProps = {
  tableId: number;
  tableName?: string;
  /** Leftover credit reported by the server when the close was refused. */
  balance: number;
  currency: string;
  onCancel: () => void;
  /** Called after the table was closed and its leftover credit settled. */
  onClosed: () => void | Promise<void>;
};

/**
 * The club keeps unused table credit as revenue at the normal tax rate
 * ("Unused table credit" on the receipt). The tip option is not offered in
 * the POS. See docs/plans/table_credit/table_lifecycle_and_forfeit.md.
 */
export const TableCreditCloseDialog =
  defineComponent<TableCreditCloseDialogProps>({
    name: "TableCreditCloseDialog",
    props: [
      "tableId",
      "tableName",
      "balance",
      "currency",
      "onCancel",
      "onClosed",
    ],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();
      const toast = useToast();
      // Another device may spend credit while this is open; the server then
      // refuses the stale amount and we show the new one.
      const balance = ref(props.balance);
      const closing = ref(false);

      const money = (value: number) =>
        formatPrice(value, true, 2, props.currency);

      const close = async () => {
        closing.value = true;
        try {
          await trpc.tableCredit.closeTable.mutate({
            tableId: props.tableId,
            mode: "revenue",
            amount: balance.value,
          });
          await props.onClosed();
        } catch (e: any) {
          const parsed = parseTableCreditError(e?.message);
          if (
            parsed &&
            (parsed.code === "settle_amount_changed" ||
              parsed.code === "settlement_required")
          ) {
            balance.value = parsed.balance;
            toast.showToast(t("pos.table_credit.balance_changed"), {
              type: "info",
            });
          } else {
            toast.showToast(e?.message || "Failed to close table", {
              type: "error",
            });
          }
        } finally {
          closing.value = false;
        }
      };

      return () => (
        <LemmonModal
          title={
            props.tableName
              ? `${t("pos.table_credit.close_title")} · ${props.tableName}`
              : t("pos.table_credit.close_title")
          }
          onClose={props.onCancel}
        >
          <div class="flex flex-col gap-3 p-4">
            <p>
              {t("pos.table_credit.unused_credit").replace(
                "{0}",
                money(balance.value),
              )}
            </p>
            {/* Revenue is the only choice and is always sent; the card is
                kept but hidden. */}
            <div class="hidden flex items-start gap-3 rounded-xl border border-primary bg-primary/10 p-3">
              <span class="material-symbols-outlined mt-0.5 text-primary">
                receipt_long
              </span>
              <span class="flex flex-col">
                <span class="font-medium">
                  {t("pos.table_credit.keep_as_revenue")}
                </span>
                <span class="text-sm text-base-content/60">
                  {t("pos.table_credit.keep_as_revenue_hint")}
                </span>
              </span>
            </div>
            <div class="flex justify-between gap-2 pt-2">
              <button
                type="button"
                class="btn btn-neutral"
                onClick={() => props.onCancel()}
              >
                {t("general.words.cancel")}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                disabled={closing.value}
                onClick={close}
              >
                {closing.value ? (
                  <span class="loading loading-spinner" />
                ) : (
                  t("pos.table_credit.close_table")
                )}
              </button>
            </div>
          </div>
        </LemmonModal>
      );
    },
  });
